import { useState, useMemo, useEffect, useCallback } from 'react';
import { Crosshair, Plus, Clock, CircleCheck as CheckCircle2, ChevronDown, ChevronRight, CircleAlert as AlertCircle, Pencil, MessageSquare } from 'lucide-react';
import ObjectiveTreeView from './ObjectiveTreeView';
import CycleCreator from './CycleCreator';
import type { MissionControlConfig, MissionControlCycle, MissionControlObjective, MissionControlObjectiveContact, Account, Contact } from '../../types';
import type { EnrichedCycle } from '../../pages/app/MissionControl';

interface Props {
  config: MissionControlConfig | null;
  myCycles: MissionControlCycle[];
  currentCycle: EnrichedCycle | null;
  myAccounts: Account[];
  myContacts: Contact[];
  orgId: string;
  userId: string;
  onRefresh: () => void;
  overrideLeaderId?: string;
  contactPriorities?: Record<string, string>;
  // When false (a leader viewing one of their reports), the member's own
  // create/edit/status controls are hidden — the leader's approve/review
  // actions live in LeaderMissionView. Defaults to true (the member's own view).
  editable?: boolean;
}

const STATUS_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  draft: { label: 'Draft', color: 'text-app-muted', bg: 'bg-app-canvas' },
  submitted: { label: 'Submitted', color: 'text-app-focus', bg: 'bg-app-focus/10' },
  approved: { label: 'Approved', color: 'text-forest-700', bg: 'bg-forest-50' },
  in_progress: { label: 'In Progress', color: 'text-app-warning', bg: 'bg-app-warning/10' },
  completed: { label: 'Completed', color: 'text-forest-700', bg: 'bg-forest-50' },
  reviewed: { label: 'Reviewed', color: 'text-app-muted', bg: 'bg-app-canvas' },
};

export default function MemberMissionView({
  config, myCycles, currentCycle: initialCycle, myAccounts, myContacts, orgId, userId, onRefresh, overrideLeaderId,
  contactPriorities, editable = true,
}: Props) {
  const [showCreator, setShowCreator] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [currentCycle, setCurrentCycle] = useState<EnrichedCycle | null>(initialCycle);
  const [editObjectives, setEditObjectives] = useState<MissionControlObjective[]>([]);
  const [editObjContacts, setEditObjContacts] = useState<MissionControlObjectiveContact[]>([]);

  useEffect(() => {
    setCurrentCycle(initialCycle);
  }, [initialCycle]);

  const hasActiveCycle = currentCycle && currentCycle.status !== 'reviewed' && currentCycle.status !== 'completed';
  const canEdit = editable && currentCycle && (currentCycle.status === 'draft' || currentCycle.status === 'submitted' || currentCycle.status === 'approved' || currentCycle.status === 'in_progress');
  const pastCycles = useMemo(() => myCycles.filter(c => c.status === 'reviewed' || c.status === 'completed'), [myCycles]);

  const cycleStats = useMemo(() => {
    if (!currentCycle) return null;
    const totalAccounts = currentCycle.objectives.length;
    const completedAccounts = currentCycle.objectives.filter(o => o.status === 'completed').length;
    const totalContacts = currentCycle.objectives.reduce((s, o) => s + o.contacts.length, 0);
    const completedContacts = currentCycle.objectives.reduce((s, o) => s + o.contacts.filter(c => c.status === 'completed').length, 0);
    return { totalAccounts, completedAccounts, totalContacts, completedContacts };
  }, [currentCycle]);

  const daysRemaining = useMemo(() => {
    if (!currentCycle) return 0;
    const end = new Date(currentCycle.cycle_end);
    const now = new Date();
    return Math.max(0, Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
  }, [currentCycle]);

  const leaderId = config?.leader_user_id || overrideLeaderId || null;

  // A status change re-reads the member overview from AWS (which recomputes the
  // enriched current cycle) rather than re-querying Supabase in-place.
  const handleStatusChanged = useCallback(() => {
    onRefresh();
  }, [onRefresh]);

  const handleCreated = useCallback(async () => {
    setShowCreator(false);
    setIsEditing(false);
    await onRefresh();
  }, [onRefresh]);

  // The active cycle is already loaded with its objectives + objective-contacts
  // (the AWS overview returns the enriched cycle), so the edit form is seeded
  // directly from it — no extra round-trip. EnrichedObjective/-Contact extend
  // their base types, so the arrays are assignable as-is.
  function handleStartEdit() {
    if (!currentCycle) return;
    setEditObjectives(currentCycle.objectives);
    setEditObjContacts(currentCycle.objectives.flatMap(o => o.contacts));
    setIsEditing(true);
    setShowCreator(true);
  }

  return (
    <div className="app-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="app-kicker mb-2">Weekly execution</p>
          <h1 className="font-display text-3xl font-semibold text-app-ink sm:text-4xl">Mission Control</h1>
          <p className="text-sm text-app-muted">Set objectives, track progress, stay accountable</p>
        </div>
        {editable && !showCreator && !hasActiveCycle && (
          <button
            onClick={() => { setIsEditing(false); setShowCreator(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-forest-600 rounded-lg hover:bg-forest-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Objectives
          </button>
        )}
      </div>

      {!config && !overrideLeaderId && !hasActiveCycle && !showCreator && (
        <div className="max-w-lg mx-auto mt-16 text-center">
          <div className="w-16 h-16 bg-app-warning/10 rounded-lg flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-app-warning" />
          </div>
          <h2 className="text-lg font-semibold text-app-ink mb-2">Waiting for Leader Configuration</h2>
          <p className="text-sm text-app-muted leading-relaxed">
            Your team leader hasn't set up the objective cadence yet.
            Once they configure Mission Control, you'll be prompted to define your weekly objectives.
          </p>
        </div>
      )}

      {editable && (config || overrideLeaderId) && !hasActiveCycle && !showCreator && (
        <div className="max-w-lg mx-auto mt-12 text-center">
          <div className="w-16 h-16 bg-forest-50 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Crosshair className="w-8 h-8 text-forest-600" />
          </div>
          <h2 className="text-lg font-semibold text-app-ink mb-2">Ready to Set Objectives</h2>
          <p className="text-sm text-app-muted leading-relaxed mb-6">
            {config
              ? <>Your leader expects objectives by <span className="font-medium text-app-ink capitalize">{config.objectives_due_day}</span>.
                {config.min_accounts && ` Aim for ${config.min_accounts} accounts`}
                {config.min_contacts_per_account && ` with ${config.min_contacts_per_account} contacts each.`}</>
              : 'Select the accounts and contacts you want to target this cycle.'}
          </p>
          <button
            onClick={() => { setIsEditing(false); setShowCreator(true); }}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-forest-600 rounded-lg hover:bg-forest-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Define This Week's Objectives
          </button>
        </div>
      )}

      {showCreator && (
        <CycleCreator
          accounts={myAccounts}
          contacts={myContacts}
          config={config}
          contactPriorities={contactPriorities}
          orgId={orgId}
          userId={userId}
          leaderId={leaderId}
          onCreated={handleCreated}
          onCancel={() => { setShowCreator(false); setIsEditing(false); }}
          existingCycleId={isEditing ? currentCycle?.id : undefined}
          existingObjectives={isEditing ? editObjectives : undefined}
          existingObjectiveContacts={isEditing ? editObjContacts : undefined}
        />
      )}

      {hasActiveCycle && currentCycle && !showCreator && (
        <div className="space-y-6">
          {/* Leader feedback banner */}
          {currentCycle.status === 'draft' && currentCycle.leader_notes && (
            <div className="bg-app-warning/10 rounded-lg border border-app-warning p-4 flex items-start gap-3">
              <MessageSquare className="w-5 h-5 text-app-warning shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-app-warning">Changes Requested</p>
                <p className="text-sm text-app-warning mt-1">{currentCycle.leader_notes}</p>
                <p className="text-xs text-app-warning mt-2">Please update your objectives and resubmit for approval.</p>
              </div>
            </div>
          )}

          {/* Cycle header */}
          <div className="bg-app-surface rounded-lg border border-app-line p-5">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-forest-50 flex items-center justify-center">
                  <Crosshair className="w-5 h-5 text-forest-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-app-ink">
                      {new Date(currentCycle.cycle_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} to {new Date(currentCycle.cycle_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    </h3>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_LABELS[currentCycle.status]?.bg} ${STATUS_LABELS[currentCycle.status]?.color}`}>
                      {STATUS_LABELS[currentCycle.status]?.label}
                    </span>
                  </div>
                  {currentCycle.leader_notes && (
                    <p className="text-xs text-app-muted mt-1">{currentCycle.leader_notes}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-6">
                {cycleStats && (
                  <>
                    <div className="text-center">
                      <p className="text-lg font-bold text-app-ink">{cycleStats.completedAccounts}/{cycleStats.totalAccounts}</p>
                      <p className="text-[10px] text-app-muted">Accounts</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-app-ink">{cycleStats.completedContacts}/{cycleStats.totalContacts}</p>
                      <p className="text-[10px] text-app-muted">Contacts</p>
                    </div>
                  </>
                )}
                <div className="text-center">
                  <div className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5 text-app-muted" />
                    <p className="text-lg font-bold text-app-ink">{daysRemaining}</p>
                  </div>
                  <p className="text-[10px] text-app-muted">Days Left</p>
                </div>

                {canEdit && (
                  <button
                    onClick={handleStartEdit}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-forest-700 bg-forest-50 rounded-lg hover:bg-forest-100 transition-colors"
                  >
                    <Pencil className="w-3 h-3" />
                    Edit
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Tree view */}
          <ObjectiveTreeView
            objectives={currentCycle.objectives}
            readOnly={!editable}
            onStatusChanged={handleStatusChanged}
          />
        </div>
      )}

      {/* Past cycles */}
      {pastCycles.length > 0 && (
        <div className="mt-8">
          <button
            onClick={() => setHistoryOpen(!historyOpen)}
            className="flex items-center gap-2 text-sm text-app-muted hover:text-app-ink transition-colors mb-3"
          >
            {historyOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Past Cycles ({pastCycles.length})
          </button>
          {historyOpen && (
            <div className="space-y-2">
              {pastCycles.map(c => (
                <div key={c.id} className="bg-app-surface rounded-lg border border-app-line px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-forest-500" />
                    <div>
                      <p className="text-sm text-app-ink">
                        {new Date(c.cycle_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} to {new Date(c.cycle_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </p>
                      {c.leader_notes && <p className="text-xs text-app-muted mt-0.5">{c.leader_notes}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_LABELS[c.status]?.bg} ${STATUS_LABELS[c.status]?.color}`}>
                      {STATUS_LABELS[c.status]?.label}
                    </span>
                    <div className="text-right">
                      <p className="text-sm font-bold text-app-ink">{Math.round(c.completion_score)}%</p>
                      <p className="text-[10px] text-app-muted">Score</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
